import { useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { Priority, WorkType } from '@rundown/types';
import { Button, Modal } from '../../components';
import { queryKeys } from '../../lib/queryKeys';
import { Composer, type ComposerHandle } from '../composer/Composer';
import { deliveryProjectsQueryOptions } from '../projects/api';
import { createRequestManually } from './api';
import { PRIORITY_LABELS, TYPE_LABELS } from './labels';

/**
 * Noorix filing work directly, with no AI in the way.
 *
 * The ask box is built for somebody describing a symptom in their own words:
 * it reads what they wrote, asks a question or two if something is unclear,
 * proposes a classification, and leaves the request for Noorix to confirm.
 * That is the right shape for a client and the wrong shape for the team — a
 * bug found in standup, a piece of internal maintenance, a follow-up agreed on
 * a call already HAS its classification, in the head of the person typing it.
 * Sending it through intake spends a model call to be told what the author
 * knew, then queues it for that same person to confirm to themselves.
 *
 * So this is a form, unapologetically, with the four fields the AI would have
 * proposed. What it creates is a settled, already-reviewed request that
 * appears in the lists immediately — see `manual` in requests.service.ts.
 *
 * It does NOT offer sprint, owner or project. Those are placement decisions
 * that belong on the board and the planning screen, next to the load they
 * affect; putting them in a create form invites someone to commit work to a
 * week without seeing what is already in it.
 */

const FIELD =
  'w-full min-w-0 rounded-control border border-line bg-base px-2.5 py-1.5 fs-13 text-ink placeholder:text-faint';

export function NewRequestDialog({
  workspaceId,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  // The same editor the request description is edited in afterwards, so what
  // is written here and what is read there are one document — a pasted curl
  // stays a code block instead of arriving as a paragraph somebody has to
  // reformat. The content lives in the editor and is pulled at save time.
  const editorRef = useRef<ComposerHandle>(null);
  const projects = useQuery(deliveryProjectsQueryOptions(workspaceId));
  // Remounts the editor on each open, so a cancelled draft does not come back
  // the next time the dialog is opened.
  const [editorKey, setEditorKey] = useState(0);
  const [type, setType] = useState<WorkType>('bug');
  const [projectId, setProjectId] = useState('');
  const [priority, setPriority] = useState<Priority>('normal');
  const [hours, setHours] = useState('');
  const [visibleToClient, setVisibleToClient] = useState(true);

  // Internal work is the one type whose existence is not the client's
  // business, and the database enforces that with a constraint. Shown as a
  // locked No rather than silently overridden after the fact.
  const internal = type === 'internal';
  const clientSees = internal ? false : visibleToClient;

  const create = useMutation({
    mutationFn: () =>
      createRequestManually(workspaceId, {
        title: title.trim(),
        // Empty string, not the document, when nothing was written: the server
        // takes `descriptionDoc` when it is there and falls back to this.
        description: '',
        descriptionDoc: editorRef.current?.isEmpty() === false ? editorRef.current.getContent() : undefined,
        type,
        projectId: projectId === '' ? null : projectId,
        requestedPriority: priority,
        approxHours: hours.trim() === '' ? 0 : Number(hours),
        visibleToClient: clientSees,
      }),
    onSuccess: async (request) => {
      // Everything that counts requests: the lists, the backlog it was
      // appended to, and the board it now sits in Triage on.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.requests(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.backlog(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.board(workspaceId) }),
      ]);
      onOpenChange(false);
      reset();
      // Straight to it, because filing something is almost never the last
      // thing you wanted to do to it — the owner, the sprint and the
      // conversation are all one screen away.
      navigate(`/requests/${request.id}`);
    },
  });

  const reset = () => {
    setTitle('');
    setEditorKey((key) => key + 1);
    setType('bug');
    setProjectId('');
    setPriority('normal');
    setHours('');
    setVisibleToClient(true);
  };

  const hoursValid = hours.trim() === '' || (Number.isFinite(Number(hours)) && Number(hours) >= 0);
  const ready = title.trim().length >= 3 && hoursValid && !create.isPending;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) create.mutate();
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
      title="New request"
      description="Filed directly, with the classification you set. No AI, and nothing to confirm afterwards."
      footer={
        <>
          <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" disabled={!ready} onClick={() => create.mutate()}>
            {create.isPending ? 'Creating…' : 'Create request'}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-3.5">
        <label className="flex flex-col gap-1">
          <span className="fs-11 text-muted">Title</span>
          <input
            autoFocus
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="What needs doing, in a line"
            className={FIELD}
          />
        </label>

        <div className="flex flex-col gap-1">
          <span className="fs-11 text-muted">Detail</span>
          {/* The one editor, same as the thread, the ask box and the
              description. `key` remounts it to clear between requests, which
              is also how its initial content is re-read. */}
          <Composer
            key={editorKey}
            ref={editorRef}
            workspaceId={workspaceId}
            toolbar
            initialContent={null}
            ariaLabel="Detail"
            placeholder="Steps, context, links — whatever the person picking this up will need."
            onPasteFiles={() => false}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <label className="flex min-w-0 flex-col gap-1">
            <span className="fs-11 text-muted">Type</span>
            <select
              value={type}
              onChange={(event) => setType(event.target.value as WorkType)}
              className={FIELD}
            >
              {(Object.keys(TYPE_LABELS) as WorkType[]).map((value) => (
                <option key={value} value={value}>
                  {TYPE_LABELS[value]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex min-w-0 flex-col gap-1">
            <span className="fs-11 text-muted">Priority</span>
            <select
              value={priority}
              onChange={(event) => setPriority(event.target.value as Priority)}
              className={FIELD}
            >
              {(Object.keys(PRIORITY_LABELS) as Priority[]).map((value) => (
                <option key={value} value={value}>
                  {PRIORITY_LABELS[value]}
                </option>
              ))}
            </select>
          </label>

          {/* A select, not the free-text "Area" box this replaced: a project
              is a row, and typing its name is how "Payments" and "Payment"
              both end up existing. Making one is Settings' job — this form
              files work under what is already there. */}
          <label className="flex min-w-0 flex-col gap-1">
            <span className="fs-11 text-muted">Project</span>
            <select
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
              disabled={projects.isPending}
              className={FIELD}
            >
              <option value="">None</option>
              {(projects.data ?? [])
                .filter((project) => !project.archived)
                .map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
            </select>
          </label>

          <label className="flex min-w-0 flex-col gap-1">
            <span className="fs-11 text-muted">Approx hours</span>
            <input
              value={hours}
              inputMode="decimal"
              onChange={(event) => setHours(event.target.value)}
              placeholder="0"
              className={`${FIELD} numeric`}
            />
          </label>
        </div>

        <label className="flex min-w-0 flex-col gap-1">
          <span className="fs-11 text-muted">Visible to client</span>
          <select
            value={clientSees ? 'yes' : 'no'}
            disabled={internal}
            onChange={(event) => setVisibleToClient(event.target.value === 'yes')}
            className={`${FIELD} disabled:opacity-60`}
          >
            <option value="yes">Yes — it shows in their portal</option>
            <option value="no">No — internal only</option>
          </select>
          {internal && <span className="fs-11 text-faint">Internal work is never shown to the client.</span>}
        </label>

        {!hoursValid && <p className="fs-12 text-danger">Hours must be a number.</p>}
        {create.error !== null && (
          <p role="alert" className="rounded-md bg-danger-tint px-3 py-2 fs-12.5 text-danger">
            {create.error.message}
          </p>
        )}

        {/* Submits on Enter from any field; the footer button is the visible
            one, so this stays out of the layout. */}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}
